/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { getIdToken } from "../utils/credentialsHelper";
import { mapLiveAnalysisDataToBoltingData } from "../apis/apiMappers";
import { BoltingData, LiveAnalysisDetailData } from "../apis/types";
import moment from "moment";

export interface LiveAnalysisState {
  status: 'idle' | 'loading' | 'loadingById' | 'failed';
  dataById: BoltingData | null;
  sinceDate: number;
  sinceUpdateDate: number;
  counts: any;
  items: any;
  filterAnomalies: boolean;
}

const initialState: LiveAnalysisState = {
  status: 'idle',
  dataById: null,
  sinceDate: moment().startOf('day').unix(),
  sinceUpdateDate: moment().unix(),
  counts: null,
  items: null,
  filterAnomalies: false,
};

export const fetchLiveAnalysisData = createAsyncThunk('liveAnalysis/fetchLiveAnalysisData',
  async ({ pageSize, pageNumber, since = false } : { pageSize?: number, pageNumber?: number, since?: boolean}, thunkAPI) => {
    const currentUser = (thunkAPI.getState() as RootState).user.currentUser?.userId;
    const sinceDate = (thunkAPI.getState() as RootState).liveAnalysis.sinceDate;
    const sinceUpdateDate = (thunkAPI.getState() as RootState).liveAnalysis.sinceUpdateDate;
    const counts = (thunkAPI.getState() as RootState).liveAnalysis.counts;
    const filterAnomalies = (thunkAPI.getState() as RootState).liveAnalysis.filterAnomalies;

    if (!currentUser) {
      return { items: []};
    }

    const idToken = await getIdToken();
    
    const time = since ? sinceUpdateDate : sinceDate;
    let paginationString = '';
    if(pageSize && pageNumber) {
      paginationString = `&page_size=${pageSize}&page_number=${pageNumber}`;
    }
    if(filterAnomalies) {
      paginationString += `&filter_anomalies=${filterAnomalies}`;
    }


    const url = `${process.env.REACT_APP_REST_API_ROOT_URL}/live-connection/bolting-data?since=${(time)}${paginationString}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
    });
    const result = await response.json();
    if(since !== true) {
      return { items: result.body.items, counts: result.body.counts, lastTimestamp: result.body.lastTimestamp, };
    }
    const newCounts = {
      NOK: result.body.counts.NOK + (counts?.NOK || 0),
      OK: result.body.counts.OK + (counts?.OK || 0),
      Anomalies: result.body.counts.Anomalies + (counts?.Anomalies || 0),
      Total: result.body.counts.Total + (counts?.Total || 0),
      NewTotalCount: result.body.counts.Total,
    };
    return { items: result.body.items, counts: newCounts, lastTimestamp: result.body.lastTimestamp, };
  }
);


export const fetchLiveAnalysisDataById = createAsyncThunk('liveAnalysis/fetchLiveAnalysisDataById',
  async ({ id } : { id : string }, thunkAPI) => {
    const currentUser = (thunkAPI.getState() as RootState).user.currentUser?.userId;

    if (!currentUser) {
      return [];
    }

    const idToken = await getIdToken();
    
    const url =
      `${process.env.REACT_APP_REST_API_ROOT_URL}/live-connection/bolting-data/${id}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
    });


    const result = await response.json();

    return (mapLiveAnalysisDataToBoltingData(result.body.item as LiveAnalysisDetailData));
  }
);


export const liveAnalysisSlice = createSlice({
  name: 'liveAnalysis',
  initialState,
  reducers: {
    changeSinceDate: (state, action) => {
      state.sinceDate = action.payload;
    },
    setFilterAnomalies: (state, action) => {
      state.filterAnomalies = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveAnalysisData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLiveAnalysisData.fulfilled, (state, action) => {
        console.log('Live analysis data fetched:', action.payload.lastTimestamp);
        state.status = 'idle';
        state.items = action.payload.items;
        const delta = 1e-6;
        if(action.payload.counts) {
          state.counts = action.payload.counts;
        }
        if (action.payload.counts?.NewTotalCount > 0) {
          state.sinceUpdateDate = action.payload.lastTimestamp + delta;
        }
      })
      .addCase(fetchLiveAnalysisDataById.pending, (state) => {
        state.status = 'loadingById';
      })
      .addCase(fetchLiveAnalysisDataById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.dataById = action.payload as BoltingData;
      }
      );
  }
});

export default liveAnalysisSlice.reducer;

export const { changeSinceDate, setFilterAnomalies } = liveAnalysisSlice.actions;